import type {RefObject} from 'react';
import {useEffect, useRef, useState} from 'react';

const SHADOW_TOGGLE_THRESHOLD = 3;

export interface UseLeftRightShadowObject {
  leftShadow: boolean;
  rightShadow: boolean;
}

export const useLeftRightScrollShadow = (scrollingContainer: RefObject<HTMLElement | null>, hasElementMounted: boolean): UseLeftRightShadowObject => {
  const [isPanelInitialized, setIsPanelInitialized] = useState(false);
  const [leftShadow, setLeftShadow] = useState(false);
  const [rightShadow, setRightShadow] = useState(false);
  const leftShadowRef = useRef<boolean>(false);
  const rightShadowRef = useRef<boolean>(false);

  const handleHorizontalScroll = (): void => {
    const scrollLeft = scrollingContainer.current?.scrollLeft ? scrollingContainer.current.scrollLeft : 0;
    const clientWidth = scrollingContainer.current?.clientWidth ? scrollingContainer.current.clientWidth : 0;
    const scrollWidth = scrollingContainer.current?.scrollWidth ? scrollingContainer.current.scrollWidth : 0;

    let newLeftShadow = leftShadowRef.current;
    let newRightShadow = rightShadowRef.current;

    if (scrollWidth > clientWidth) {
      if (scrollLeft > SHADOW_TOGGLE_THRESHOLD) {
        newLeftShadow = true;
      }
      if (scrollLeft <= SHADOW_TOGGLE_THRESHOLD) {
        newLeftShadow = false;
      }

      if (scrollLeft + clientWidth + SHADOW_TOGGLE_THRESHOLD >= scrollWidth) {
        newRightShadow = false;
      }

      if (scrollLeft + clientWidth + SHADOW_TOGGLE_THRESHOLD < scrollWidth) {
        newRightShadow = true;
      }
    } else {
      newLeftShadow = false;
      newRightShadow = false;
    }

    if (newLeftShadow !== leftShadowRef.current) {
      leftShadowRef.current = newLeftShadow;
      setLeftShadow(newLeftShadow);
    }

    if (newRightShadow !== rightShadowRef.current) {
      rightShadowRef.current = newRightShadow;
      setRightShadow(newRightShadow);
    }
  };

  const handleScroll = (): void => {
    if (scrollingContainer && scrollingContainer.current) {
      handleHorizontalScroll();
    }
  };

  useEffect(() => {
    if (!scrollingContainer.current || !hasElementMounted) return;

    const resizeObserver = new ResizeObserver(() => {
      handleHorizontalScroll();
    });

    resizeObserver.observe(scrollingContainer.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [handleHorizontalScroll, scrollingContainer, hasElementMounted]);

  useEffect(() => {
    handleScroll();
    scrollingContainer?.current?.addEventListener(
      'scroll',
      window.PMW.debounce(() => {
        handleScroll();
      }, 100),
      {passive: true}
    );
    if (scrollingContainer.current && !isPanelInitialized) {
      setIsPanelInitialized(true);
    }

    return () => {
      scrollingContainer?.current?.removeEventListener('scroll', handleScroll);
    };
  }, [scrollingContainer.current, hasElementMounted]);

  return {
    leftShadow,
    rightShadow,
  };
};
